import RestService from './RestService';
import PersonalIdentificationType from '../model/PersonalIdentificationType';

const fetchCurrentAddressDetails = async (addressId) => {
  try {
    const response = await RestService.get(`/api/addressChecker/addressLookup/residential?addressId=${addressId}`);
    return { status: response.status.code, result: response.entity };
  } catch (error) {
    return { status: error.status.code, message: error.status.text };
  }
};

const toDate = (year, month, day) => {
  let mm = month;
  if (month.length === 1) {
    mm = `0${month}`;
  }
  if (day) {
    let dd = day;
    if (day.length === 1) {
      dd = `0${day}`;
    }
    return `${year}-${mm}-${dd}`;
  }
  return `${year}-${mm}`;
};

const toNumberOfMonths = (fromMonth, fromYear, toMonth, toYear) => {
  const intFromMonth = Number.parseInt(fromMonth, 10);
  const intFromYear = Number.parseInt(fromYear, 10);
  const intToMonth = Number.parseInt(toMonth, 10);
  const intToYear = Number.parseInt(toYear, 10);
  if (intFromYear > intToYear) {
    return 0;
  }
  if (intFromYear === intToYear && intFromMonth > intToMonth) {
    return 0;
  }
  return ((intToYear - intFromYear) * 12) + (intToMonth - intFromMonth);
};

const getDateOfBirth = (creditAssessment) => {
  if (!creditAssessment.dateOfBirthDay) {
    return undefined;
  }
  return toDate(creditAssessment.dateOfBirthYear, creditAssessment.dateOfBirthMonth, creditAssessment.dateOfBirthDay);
};

const getDriverLicense = (creditAssessment) => {
  if (creditAssessment.personalIdentificationType !== PersonalIdentificationType.NZ_DRIVER_LICENSE
      || !creditAssessment.nzDriverLicense) {
    return undefined;
  }
  const nzDriverLicense = creditAssessment.nzDriverLicense;
  const expiryDate = nzDriverLicense.expiryDateDay
    ? toDate(nzDriverLicense.expiryDateYear, nzDriverLicense.expiryDateMonth, nzDriverLicense.expiryDateDay)
    : undefined;
  return {
    licenseNumber: nzDriverLicense.licenseNumber,
    licenseVersionNumber: nzDriverLicense.licenseVersionNumber,
    expiryDate,
  };
};

const getPassport = (creditAssessment) => {
  if (creditAssessment.personalIdentificationType !== PersonalIdentificationType.NZ_PASSPORT
      || !creditAssessment.nzPassport) {
    return undefined;
  }
  const nzPassport = creditAssessment.nzPassport;
  const expiryDate = nzPassport.expiryDateDay
    ? toDate(nzPassport.expiryDateYear, nzPassport.expiryDateMonth, nzPassport.expiryDateDay)
    : undefined;
  return {
    passportNumber: nzPassport.passportNumber,
    expiryDate,
  };
};

const getCurrentAddress = (creditAssessment) => {
  const address = creditAssessment.currentAddress ? creditAssessment.currentAddress : undefined;
  if (address) {
    Object.keys(address).forEach((key) => {
      if (address[key] == null) {
        delete address[key];
      }
    });
  }
  const yearsAsMonths = creditAssessment.yearsAtCurrentAddress
    ? Number.parseInt(creditAssessment.yearsAtCurrentAddress, 10) * 12
    : 0;
  const monthsAsMonths = creditAssessment.monthsAtCurrentAddress
    ? Number.parseInt(creditAssessment.monthsAtCurrentAddress, 10)
    : 0;
  const monthsAtCurrentAddress = yearsAsMonths + monthsAsMonths;
  return {
    address,
    monthsAtCurrentAddress
  };
};

const toMonthAndYear = (month, year) => {
  if (!month && !year) {
    return undefined;
  }
  const monthString = `00${!month ? '' : month}`.slice(-2);
  const yearString = `0000${!year ? '' : year}`.slice(-4);
  return `${monthString}/${yearString}`;
};

const getEmployerDuration = (creditAssessment) => {
  if (!creditAssessment.dateStartedCurrentMonth) {
    return undefined;
  }
  const now = new Date();
  return toNumberOfMonths(
    creditAssessment.dateStartedCurrentMonth,
    creditAssessment.dateStartedCurrentYear,
    now.getMonth() + 1,
    now.getFullYear());
};

const getEmployer = (creditAssessment) => {
  if (!creditAssessment.employer) {
    return undefined;
  }
  return {
    name: creditAssessment.employer,
    startedDate: toMonthAndYear(creditAssessment.dateStartedCurrentMonth, creditAssessment.dateStartedCurrentYear),
    duration: getEmployerDuration(creditAssessment)
  };
};

const getPreviousEmployerDuration = (creditAssessment) => {
  if (!creditAssessment.dateStartedPreviousMonth) {
    return undefined;
  }
  return toNumberOfMonths(
    creditAssessment.dateStartedPreviousMonth,
    creditAssessment.dateStartedPreviousYear,
    creditAssessment.dateStartedCurrentMonth,
    creditAssessment.dateStartedCurrentYear);
};

const getPreviousEmployer = (creditAssessment) => {
  if (!creditAssessment.previousEmployer) {
    return undefined;
  }
  return {
    name: creditAssessment.previousEmployer,
    startedDate: toMonthAndYear(creditAssessment.dateStartedPreviousMonth, creditAssessment.dateStartedPreviousYear),
    duration: getPreviousEmployerDuration(creditAssessment)
  };
};

const getOrder = (selectedPlan, modemPrice) => {
  if (!(selectedPlan && selectedPlan.price) && !modemPrice) {
    return undefined;
  }
  return {
    planPrice: selectedPlan.price === null ? undefined : selectedPlan.price,
    modemPrice: modemPrice === null ? undefined : modemPrice
  };
};

const getRequestBody = (values, selectedPlan, modemPrice, isRetry) => {
  const email = values.email;
  const applicationId = values.caApplicationId;
  const creditAssessment = values.creditAssessment;

  return {
    applicationId,
    agreeToCreditAssessment: creditAssessment.agreeToCreditAssessment,
    firstName: creditAssessment.firstName,
    middleName: creditAssessment.middleName ? creditAssessment.middleName : undefined,
    lastName: creditAssessment.lastName,
    email,
    dateOfBirth: getDateOfBirth(creditAssessment),
    isNzResidentOrCitizen: creditAssessment.isNzResidentOrCitizen === 'yes',
    personalIdentificationType: creditAssessment.personalIdentificationType,
    nzDriverLicense: getDriverLicense(creditAssessment),
    nzPassport: getPassport(creditAssessment),
    currentAddress: getCurrentAddress(creditAssessment),
    housingStatus: creditAssessment.housingStatus,
    occupationStatus: creditAssessment.occupationStatus,
    employmentStatus: creditAssessment.employmentStatus,
    currentEmployer: getEmployer(creditAssessment),
    previousEmployer: getPreviousEmployer(creditAssessment),
    order: getOrder(selectedPlan, modemPrice),
    retryTimestamp: isRetry ? new Date() : undefined
  };
};

const submitCreditAssessment = (values, selectedPlan, modemPrice, isRetry) => {
  try {
    const entity = getRequestBody(values, selectedPlan, modemPrice, isRetry);
    return RestService.post('/api/creditAssessment/sendRequest', entity);
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
    throw e;
  }
};

const queryCreditAssessmentResult = (values) => {
  try {
    const entity = { applicationId: values.caApplicationId };
    return RestService.post('/api/creditAssessment/queryResult', entity);
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
    throw e;
  }
};

const CreditAssessmentService = {
  fetchCurrentAddressDetails,
  submitCreditAssessment,
  queryCreditAssessmentResult
};

export default CreditAssessmentService;



/** WEBPACK FOOTER **
 ** ./src/services/CreditAssessmentService.js
 **/